import { Text } from '@chakra-ui/react';
import { useRouter } from 'next/navigation';
import { useContext, useState } from 'react';
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
} from '~/components/ui/dialog';
import {
  ProgressCircleRing,
  ProgressCircleRoot,
} from '~/components/ui/progress-circle';
import { toaster } from '~/components/ui/toaster';
import { useI18nLn } from '~/hooks/store';
import { getDeviceType } from '~/lib/device';
import { gachaCheckin } from '~/src/services/arcApi/gacha_check_in';
import { isActiveToken } from '~/src/services/users';
import { RootContext } from '~/store';
import { GachaDetail } from '../../../types/resources/gachaDetail';
import { WebrollButton } from '../buttons/WebrollButton';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCheckinSuccess?: () => void;
  returnUrl?: string;
  checkinTargetGachaId: GachaDetail['id'];
};

export const GpsCheckinModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onCheckinSuccess,
  returnUrl,
  checkinTargetGachaId,
}) => {
  const { t } = useI18nLn();
  const { token } = useContext(RootContext);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const router = useRouter();

  const handleEndCheckinProcess = () => {
    onClose();
    setIsCheckingIn(false);
  };

  const handleCheckin = async () => {
    setIsCheckingIn(true);

    if (!isActiveToken(token) || token == null) return;

    try {
      const res = await gachaCheckin(token);

      if (
        res.data?.status == 200 &&
        res.data?.body.checked_gacha_ids.includes(checkinTargetGachaId)
      ) {
        returnUrl && router.push(returnUrl);
        toaster.success({
          title: t('pages.check_in.result.success'),
          duration: 3000,
        });
        handleEndCheckinProcess();
        onCheckinSuccess && onCheckinSuccess();
      } else {
        toaster.error({
          title: t('pages.check_in.result.not_nearby.title'),
          description: t('pages.check_in.result.not_nearby.description'),
        });
        handleEndCheckinProcess();
      }
    } catch (e) {
      if (e instanceof GeolocationPositionError) {
        if (e.code == 1) {
          toaster.error({
            title: t('pages.check_in.result.permission_denied.title'),
            description:
              getDeviceType() == 'iOS'
                ? t('pages.check_in.result.permission_denied.ios_description')
                : t(
                    'pages.check_in.result.permission_denied.android_description'
                  ),
          });
          handleEndCheckinProcess();
        } else {
          toaster.error({
            title: t('pages.check_in.result.get_location_failed.title'),
            description: t(
              'pages.check_in.result.get_location_failed.description'
            ),
          });
          handleEndCheckinProcess();
        }
      }
    }
  };

  return (
    <DialogRoot open={isOpen} onInteractOutside={onClose}>
      <DialogBackdrop />
      <DialogContent mx="16px">
        <DialogHeader>
          <Text fontSize="16px" fontWeight="thin">
            {t('pages.check_in.modal.title')}
          </Text>
          <DialogCloseTrigger onClick={onClose} />
        </DialogHeader>
        <DialogBody
          py={4}
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDir="column"
        >
          {isCheckingIn ? (
            <>
              <Text fontWeight="semibold">
                {t('pages.check_in.modal.check_in_progress_1')}
              </Text>
              <Text fontWeight="semibold">
                {t('pages.check_in.modal.check_in_progress_2')}
              </Text>
              <ProgressCircleRoot
                py={12}
                value={null}
                color="primary"
                size="xl"
              >
                <ProgressCircleRing />
              </ProgressCircleRoot>
            </>
          ) : (
            <>
              <Text boxSize="100px" fontSize="14px" fontWeight="thin">
                {t('pages.check_in.modal.description')}
              </Text>
            </>
          )}
        </DialogBody>
        <DialogFooter justifyContent="center">
          <WebrollButton loading={isCheckingIn} onClick={handleCheckin}>
            {t('pages.check_in.title')}
          </WebrollButton>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
